import {AnimatePresence, motion} from "framer-motion"
import {FC, useCallback, useEffect, useState} from "react"

import chevronRight from "@frontend/design/icons/chevron-right.svg"
import {useSession} from "@frontend/session"
import {
  getHostPartPrice,
  navigateToPart,
  toPartPriceRequestItem,
} from "@frontend/utils/appHostFunctions"
import {trackEvent} from "@frontend/utils/trackEvent"
import {useAddToCartMutation} from "@frontend/utils/trpc"
import {ConfirmedCompatiblePart} from "@ri2/db/client"
import {css} from "@styled-system/css"
import {hstack, vstack} from "@styled-system/patterns"

interface SuggestedPart {
  part: ConfirmedCompatiblePart
  causeName: string
  probability: number
}

interface Props {
  suggestedPart: SuggestedPart
  caseId: string
  modelNumber: string | null
  compact?: boolean
}

const getSubtitleText = (probability: number): string => {
  if (probability >= 0.9) return "This part may be needed"
  if (probability >= 0.85) return "Most likely the part you need"
  if (probability >= 0.7) return "Likely part needed"
  return "This part may be needed"
}

/**
 * Collapsible product card shown below the message bubble during diagnostics.
 * Collapsed: compact row with thumbnail, name, price, chevron.
 * Expanded: larger image, part number, Add to Cart / View Part CTA.
 */
export const SuggestedPartCard: FC<Props> = ({
  suggestedPart,
  caseId,
  modelNumber,
  compact = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const {part, probability} = suggestedPart
  const {userRcId} = useSession()
  const addToCartMutation = useAddToCartMutation()
  const [hostPrice, setHostPrice] = useState<number | undefined>()

  useEffect(() => {
    let isMounted = true
    setHostPrice(undefined)

    void getHostPartPrice(
      toPartPriceRequestItem({
        oem_number: part.manufacturerPartNumber,
        manufacturer_name: part.manufacturerName ?? "",
      }),
    ).then((value) => {
      if (!isMounted) return
      setHostPrice(typeof value === "number" ? value : undefined)
    })

    return (): void => {
      isMounted = false
    }
  }, [part, modelNumber])

  const onAddToCart = useCallback(async () => {
    trackEvent(
      compact ? "widget_add_to_cart_suggested" : "chat_add_to_cart_suggested",
    )

    const price = await getHostPartPrice(
      toPartPriceRequestItem({
        oem_number: part.manufacturerPartNumber,
        manufacturer_name: part.manufacturerName ?? "",
      }),
    )

    window.ri2?.addItemsToCart(
      [
        {
          id: part.id,
          oem_number: part.manufacturerPartNumber,
          price: typeof price === "number" ? price : undefined,
          name: part.name ?? undefined,
          model_number: modelNumber ?? "",
        },
      ],
      caseId,
    )

    addToCartMutation.mutate({
      userRcId,
      caseId,
      partId: part.id,
    })
  }, [part, caseId, modelNumber, addToCartMutation, userRcId, compact])

  const onViewPart = useCallback(() => {
    trackEvent(compact ? "widget_view_suggested_part" : "chat_view_part")
    navigateToPart(part)
  }, [part, compact])

  const hasCartIntegration = !!window.ri2?.addItemsToCart
  const canNavigate = !!window.ri2?.getPartDetailUrl || !!part.purchaseUrl
  const formattedPrice =
    typeof hostPrice === "number" ? `$${hostPrice.toFixed(2)}` : null
  const subtitle = getSubtitleText(probability)

  // Show just the name without any part number for the collapsed header
  const displayName = part.name ?? part.manufacturerPartNumber

  return (
    <div
      className={css({
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: "white",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
        maxWidth: "300px",
      })}
    >
      {/* Collapsed header row */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setIsExpanded((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            setIsExpanded((prev) => !prev)
          }
        }}
        className={css({
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: "10px",
          padding: "10px 12px",
          width: "100%",
          cursor: "pointer",
          userSelect: "none",
          _hover: {
            backgroundColor: "#FAFAFA",
          },
        })}
      >
        {/* Thumbnail */}
        {part.image && (
          <div
            onClick={
              canNavigate
                ? (e) => {
                    e.stopPropagation()
                    onViewPart()
                  }
                : undefined
            }
            className={css({
              width: "48px",
              height: "48px",
              minWidth: "48px",
              borderRadius: "8px",
              overflow: "hidden",
              backgroundColor: "white",
              border: "1px solid #EEEEEE",
              cursor: canNavigate ? "pointer" : undefined,
              flexShrink: 0,
            })}
          >
            <img
              src={part.image.url}
              alt=""
              className={css({
                width: "100%",
                height: "100%",
                objectFit: "cover",
              })}
            />
          </div>
        )}

        {/* Name + subtitle */}
        <div
          className={css({
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: "2px",
          })}
        >
          <span
            className={css({
              color: "text.primary",
              fontSize: "13px",
              fontWeight: 600,
              lineHeight: "1.3",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            })}
          >
            {displayName}
          </span>
          <span
            className={css({
              color: "text.tertiary",
              fontSize: "11px",
              lineHeight: "1.3",
            })}
          >
            {subtitle}
          </span>
        </div>

        {/* Price */}
        {formattedPrice && (
          <span
            className={css({
              color: "text.primary",
              fontWeight: 700,
              fontSize: "14px",
              whiteSpace: "nowrap",
              flexShrink: 0,
            })}
          >
            {formattedPrice}
          </span>
        )}

        {/* Chevron */}
        <img
          src={chevronRight}
          alt=""
          className={css({
            width: "14px",
            height: "14px",
            flexShrink: 0,
            transition: "transform 0.2s ease",
            transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
            opacity: 0.35,
          })}
        />
      </div>

      {/* Expandable details */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{height: 0, opacity: 0}}
            animate={{height: "auto", opacity: 1}}
            exit={{height: 0, opacity: 0}}
            transition={{type: "spring", stiffness: 300, damping: 25}}
            style={{overflow: "hidden"}}
          >
            <div
              className={css({
                borderTop: "1px solid #EEEEEE",
              })}
            >
              {/* Larger image */}
              {part.image && (
                <div
                  onClick={
                    canNavigate
                      ? (e) => {
                          e.stopPropagation()
                          onViewPart()
                        }
                      : undefined
                  }
                  className={css({
                    width: "100%",
                    height: "120px",
                    overflow: "hidden",
                    backgroundColor: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "8px",
                    cursor: canNavigate ? "pointer" : undefined,
                  })}
                >
                  <img
                    src={part.image.url}
                    alt={displayName}
                    className={css({
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    })}
                  />
                </div>
              )}

              {/* Part details + CTA */}
              <div
                className={vstack({
                  padding: 12,
                  gap: 8,
                  alignItems: "stretch",
                })}
              >
                {/* Part name */}
                <p
                  onClick={
                    canNavigate
                      ? (e) => {
                          e.stopPropagation()
                          onViewPart()
                        }
                      : undefined
                  }
                  className={css({
                    color: "text.primary",
                    fontSize: "13px",
                    fontWeight: 600,
                    margin: 0,
                    cursor: canNavigate ? "pointer" : undefined,
                    _hover: canNavigate
                      ? {textDecoration: "underline"}
                      : undefined,
                  })}
                >
                  {displayName}
                </p>

                {/* Part number */}
                <p
                  className={css({
                    color: "text.tertiary",
                    fontSize: "11px",
                    margin: 0,
                  })}
                >
                  {"Part #"}
                  {part.manufacturerPartNumber}
                </p>

                {/* Price + CTA row */}
                <div
                  className={hstack({
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 8,
                  })}
                >
                  {formattedPrice && (
                    <span
                      className={css({
                        color: "text.primary",
                        fontWeight: 700,
                        fontSize: "16px",
                      })}
                    >
                      {formattedPrice}
                    </span>
                  )}

                  {hasCartIntegration ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onAddToCart()
                      }}
                      className={css({
                        backgroundColor: "background.brand.accent",
                        color: "foreground.primaryOnSolid",
                        border: "none",
                        borderRadius: "8px",
                        paddingX: "14px",
                        paddingY: "8px",
                        fontWeight: 600,
                        fontSize: "13px",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        flex: formattedPrice ? undefined : 1,
                        _hover: {
                          opacity: 0.9,
                        },
                      })}
                    >
                      {"Add to Cart"}
                    </button>
                  ) : canNavigate ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onViewPart()
                      }}
                      className={css({
                        backgroundColor: "background.brand.accent",
                        color: "foreground.primaryOnSolid",
                        border: "none",
                        borderRadius: "8px",
                        paddingX: "14px",
                        paddingY: "8px",
                        fontWeight: 600,
                        fontSize: "13px",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        flex: formattedPrice ? undefined : 1,
                        _hover: {
                          opacity: 0.9,
                        },
                      })}
                    >
                      {"View Part"}
                    </button>
                  ) : null}
                </div>

                {part.isNoLongerAvailable && (
                  <p
                    className={css({
                      color: "#DC2626",
                      fontSize: "11px",
                      fontWeight: 500,
                      margin: 0,
                    })}
                  >
                    {"Currently unavailable"}
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
